import { Box, FormControlLabel, Switch, Typography } from '@mui/material';
import { MessageDescriptor, useIntl } from 'react-intl';
import { NotificationWorkflowChannels } from '../workflows';
import {
  notificationPreferencesEmailLabel,
  notificationPreferencesInAppLabel,
} from './notification-preferences-messages';
import { WorkflowChannelPrefs } from './notification-preferences-types';

export type NotificationPreferencesRowProps = {
  title: MessageDescriptor;
  description: MessageDescriptor;
  channels: NotificationWorkflowChannels;
  value: WorkflowChannelPrefs;
  disabled?: boolean;
  onChange: (next: WorkflowChannelPrefs) => void;
};

/**
 * One workflow row with switches only for channels the workflow supports.
 */
export function NotificationPreferencesRow({
  title,
  description,
  channels,
  value,
  disabled = false,
  onChange,
}: NotificationPreferencesRowProps) {
  const $t = useIntl().formatMessage;

  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 2,
        py: 1.5,
        borderBottom: 1,
        borderColor: 'divider',
        '&:last-child': {
          borderBottom: 0,
          pb: 0,
        },
        '&:first-of-type': {
          pt: 0,
        },
      }}
    >
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography variant="subtitle2">{$t(title)}</Typography>
        <Typography variant="body2" color="text.secondary">
          {$t(description)}
        </Typography>
      </Box>
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          flexShrink: 0,
          '& .MuiFormControlLabel-label': { typography: 'body2' },
        }}
      >
        {channels.email && (
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={value.email ?? true}
                disabled={disabled}
                onChange={(_, checked) => onChange({ ...value, email: checked })}
              />
            }
            label={$t(notificationPreferencesEmailLabel)}
          />
        )}
        {channels.inApp && (
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={value.inAppFeed ?? true}
                disabled={disabled}
                onChange={(_, checked) => onChange({ ...value, inAppFeed: checked })}
              />
            }
            label={$t(notificationPreferencesInAppLabel)}
          />
        )}
      </Box>
    </Box>
  );
}
